<template>
  <v-fade-transition mode="out-in">
    <router-view />
  </v-fade-transition>
</template>

<script>
  export default {
    name: 'App',
    metaInfo: {
      title: 'Home',
      titleTemplate: '%s | Hacker Hang',
      htmlAttrs: { lang: 'en' },
      meta: [
        { charset: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'description', content: 'Hacker Hang is an AI media company. We build AI agents, React Native apps, and social platforms, and we make VividMotion and Total Chaos.' },
        { name: 'theme-color', content: '#06041A' },
      ],
    },
  }
</script>

<style lang="sass">
  // ------------------------------------------------------------
  // Brand tokens
  // ------------------------------------------------------------
  \:root
    --hh-font-display: 'Playfair Display', Georgia, 'Times New Roman', serif
    --hh-font-body: 'Inter', ui-sans-serif, system-ui, sans-serif
    --hh-ink: #06041A
    --hh-surface: #0E0A33
    --hh-line: rgba(255, 255, 255, .08)
    --hh-text: #F3F1FF
    --hh-muted: #B7B3D9
    --hh-cyan: #78F5F5
    --hh-sky: #4FA8F5
    --hh-blue: #2540F0
    --hh-deep: #1000E8
    --hh-indigo: #4B1DE8
    --hh-violet: #A81AE8
    --hh-magenta: #E83AF0
    --hh-pink: #E63BB0
    --hh-mint: #7FF5D8
    --hh-green: #6BF06B
    --hh-red: #E8404F
    --hh-orange: #E85A2E
    --hh-yellow: #F2C63B
    // Both swatch sets, end to end. This is the one loud element on the site.
    --hh-spectrum: linear-gradient(90deg, #6BF06B 0%, #7FF5D8 10%, #78F5F5 20%, #4FA8F5 30%, #2540F0 40%, #1000E8 48%, #4B1DE8 56%, #A81AE8 64%, #E83AF0 72%, #E63BB0 80%, #E8404F 88%, #E85A2E 94%, #F2C63B 100%)
    --hh-spectrum-v: linear-gradient(180deg, #6BF06B 0%, #7FF5D8 10%, #78F5F5 20%, #4FA8F5 30%, #2540F0 40%, #1000E8 48%, #4B1DE8 56%, #A81AE8 64%, #E83AF0 72%, #E63BB0 80%, #E8404F 88%, #E85A2E 94%, #F2C63B 100%)
    --hh-ocean: linear-gradient(135deg, #1000E8 0%, #4B1DE8 40%, #A81AE8 75%, #E83AF0 100%)
    --hh-text-gradient: linear-gradient(100deg, #78F5F5 0%, #4FA8F5 30%, #A81AE8 65%, #E83AF0 100%)

  html
    scroll-behavior: smooth
    @media (prefers-reduced-motion: reduce)
      scroll-behavior: auto

  .v-application
    color: var(--hh-text)
    // Deep ink with a slow diagonal wash so the page is never flat black.
    background: radial-gradient(1200px 700px at 85% -10%, rgba(37, 64, 240, .28), transparent 60%), radial-gradient(900px 600px at -10% 40%, rgba(168, 26, 232, .18), transparent 60%), linear-gradient(180deg, #06041A 0%, #0A0628 55%, #06041A 100%) !important

  .v-application .grey--text.text--lighten-1
    color: var(--hh-muted) !important

  // ------------------------------------------------------------
  // Shared brand utilities
  // ------------------------------------------------------------
  .hh-spectrum-bar
    height: 4px
    width: 100%
    background: var(--hh-spectrum)

  .hh-spectrum-text
    display: inline-block
    background: var(--hh-spectrum)
    -webkit-background-clip: text
    background-clip: text
    color: transparent

  .hh-ocean-text
    background: var(--hh-ocean)
    -webkit-background-clip: text
    background-clip: text
    color: transparent

  .hh-brace
    font-family: var(--hh-font-display)
    font-weight: 500

  .hh-panel
    position: relative
    border: 1px solid var(--hh-line)
    border-radius: 20px
    background: rgba(14, 10, 51, .55)
    backdrop-filter: blur(12px)
    overflow: hidden

  .hh-panel--tint-ocean::before,
  .hh-panel--tint-magenta::before
    content: ''
    position: absolute
    inset: 0
    pointer-events: none

  .hh-panel--tint-ocean::before
    background: radial-gradient(600px 300px at 0% 0%, rgba(79, 168, 245, .28), transparent 65%)

  .hh-panel--tint-magenta::before
    background: radial-gradient(600px 300px at 100% 0%, rgba(232, 58, 240, .28), transparent 65%)

  .hh-muted
    color: var(--hh-muted) !important

  .hh-display
    font-family: var(--hh-font-display) !important
    font-weight: 500 !important
    letter-spacing: -0.02em !important
    line-height: 0.96 !important
    font-size: clamp(2.8rem, 6.5vw, 5.2rem) !important

  .hh-gradient-text
    background: var(--hh-text-gradient)
    -webkit-background-clip: text
    background-clip: text
    color: transparent

  .hh-h2
    font-family: var(--hh-font-display) !important
    font-weight: 500 !important
    letter-spacing: -0.02em !important
    line-height: 1.05 !important
    font-size: clamp(2.1rem, 3.8vw, 3.1rem) !important

  .hh-h2--compact
    font-size: clamp(1.8rem, 2.8vw, 2.5rem) !important

  .hh-h3
    font-family: var(--hh-font-display) !important
    font-weight: 500 !important
    letter-spacing: -0.01em !important
    line-height: 1.2 !important
    font-size: 1.5rem !important

  .hh-lead
    font-size: 1.2rem
    line-height: 1.6
    color: var(--hh-muted)
    max-width: 38em

  .hh-measure
    max-width: 40em

  // Actions read as links, not buttons. The primary one carries the brand
  // gradient in its rule; every other one gets a hairline. The
  // ".v-application a" prefix is needed to outrank the anchor colour
  // Vuetify generates from the theme's own anchor token.
  .v-application a.hh-link
    position: relative
    display: inline-block
    font-family: var(--hh-font-body)
    font-size: 1.02rem
    font-weight: 500
    line-height: 1.4
    letter-spacing: 0
    color: var(--hh-text)
    text-decoration: none
    padding-bottom: 7px
    transition: color .2s ease

    &::after
      content: ''
      position: absolute
      left: 0
      right: 0
      bottom: 0
      height: 1px
      background: rgba(255, 255, 255, .22)
      transition: background .2s ease, opacity .2s ease

    &:hover
      color: var(--hh-cyan)

      &::after
        background: var(--hh-cyan)

  .v-application a.hh-link.hh-link--accent
    &::after
      height: 2px
      background: var(--hh-ocean)
      opacity: .85

    &:hover::after
      background: var(--hh-ocean)
      opacity: 1

  @media (prefers-reduced-motion: reduce)
    .v-application a.hh-link,
    .v-application a.hh-link::after
      transition: none

  .v-btn:focus-visible
    outline: 2px solid var(--hh-cyan)
    outline-offset: 3px

  a:focus-visible
    outline: 2px solid var(--hh-cyan)
    outline-offset: 2px

  .extra-padding
    padding-bottom: 96px !important
    padding-top: 96px !important

    @media screen and (max-width: 959px)
      padding-top: 48px !important
      padding-bottom: 48px !important
</style>
